import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Space, Spin } from "antd";
import {
  createSiteIoConfigurationMutation,
  getSiteIoConfigurationQueryKey,
  patchSiteIoConfigurationMutation,
  validateSiteOptions,
} from "../../../../services/client/@tanstack/react-query.gen";
import { getSiteIoConfiguration } from "../../../../services/client/sdk.gen";
import { SiteIoConfiguration } from "../../../../services/client";
import { SiteIoConfigCard } from "./SiteIoConfigCard";
import { SiteIoConfigEditor } from "./SiteIoConfigEditor";
import { SiteIoConfigDiffModal } from "./SiteIoConfigDiffModal";
import { computeSiteIoDiff, SiteIoDraft } from "./utils/siteIoDiffUtils";
import { useNotify } from "../../../util-components/Notifications/notifications";
import { phaseRotationDeclarations } from "../../../../util/phaseRotations";
import { DisableForViewer } from "../../../common/DisableForViewer";

type SiteIoConfigSectionProps = {
  site_id: string;
};

function extractErrorMessage(error: unknown): string {
  const detail = (error as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string") {
    return detail;
  }
  if (Array.isArray(detail) && typeof detail[0]?.msg === "string") {
    return detail[0].msg;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Please try again.";
}

export const SiteIoConfigSection: React.FC<SiteIoConfigSectionProps> = ({
  site_id,
}) => {
  const notify = useNotify();
  const queryClient = useQueryClient();

  const queryKey = getSiteIoConfigurationQueryKey({ path: { site_id } });

  const {
    data: configuration,
    isLoading,
    error,
  } = useQuery<SiteIoConfiguration | null>({
    queryKey,
    queryFn: async ({ signal }) => {
      const response = await getSiteIoConfiguration({
        path: { site_id },
        signal,
      });
      if (response.response.status === 404) {
        return null;
      }
      if (response.error) {
        throw response.error;
      }
      return response.data ?? null;
    },
  });

  const notFound = configuration === null && !error;

  const { data: site } = useQuery(validateSiteOptions({ path: { site_id } }));
  const siteConfiguration = site?.configurations?.site_configuration;

  const [edit, setEdit] = useState(false);
  const [pendingData, setPendingData] = useState<SiteIoDraft | undefined>(
    undefined,
  );
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const patchMutation = useMutation({
    ...patchSiteIoConfigurationMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setPendingData(undefined);
      setConfirmOpen(false);
      setEdit(false);
      notify({ type: "success", message: "Site IO configuration updated" });
    },
    onError: (err) => {
      notify({
        type: "error",
        message: "Failed to update site IO configuration",
        description: extractErrorMessage(err),
      });
    },
  });

  const createMutation = useMutation({
    ...createSiteIoConfigurationMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setPendingData(undefined);
      setConfirmOpen(false);
      setEdit(false);
      notify({ type: "success", message: "Site IO configuration created" });
    },
    onError: (err) => {
      notify({
        type: "error",
        message: "Failed to create site IO configuration",
        description: extractErrorMessage(err),
      });
    },
  });

  const isPending = patchMutation.isPending || createMutation.isPending;

  const liveConfiguration = configuration ?? null;

  const rotationDeclarations = useMemo(
    () =>
      phaseRotationDeclarations(
        siteConfiguration,
        pendingData ?? liveConfiguration,
      ),
    [siteConfiguration, pendingData, liveConfiguration],
  );

  const diff = useMemo(
    () => computeSiteIoDiff(liveConfiguration, pendingData),
    [liveConfiguration, pendingData],
  );

  const hasChanges =
    pendingData !== undefined &&
    (diff.assets.length > 0 ||
      !!diff.controllerModeChange ||
      !!diff.phasePermutationModeChange);

  const handleSave = () => {
    if (!pendingData) {
      return;
    }
    if (liveConfiguration) {
      patchMutation.mutate({ path: { site_id }, body: pendingData });
    } else {
      createMutation.mutate({ path: { site_id }, body: pendingData });
    }
  };

  const handleToggleEdit = () => {
    if (edit) {
      setPendingData(undefined);
    }
    setEdit(!edit);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Spin />
      </div>
    );
  }

  if (error && !notFound) {
    return (
      <Alert
        type="error"
        showIcon
        message="Failed to load site IO configuration"
        description={extractErrorMessage(error)}
      />
    );
  }

  const showEditor = edit || notFound;

  return (
    <div className="w-full relative">
      <div className="flex justify-end mb-4">
        <Space>
          {showEditor && (
            <>
              <Button
                disabled={!hasChanges || isPending}
                onClick={() => setPreviewOpen(true)}
              >
                Preview
              </Button>
              <Button
                type="primary"
                disabled={!hasChanges}
                loading={isPending}
                onClick={() => setConfirmOpen(true)}
              >
                Save
              </Button>
            </>
          )}
          {!notFound && liveConfiguration && (
            <DisableForViewer>
              <Button
                type={edit ? "default" : "primary"}
                disabled={isPending}
                onClick={handleToggleEdit}
              >
                {edit ? "Cancel" : "Edit"}
              </Button>
            </DisableForViewer>
          )}
        </Space>
      </div>

      {notFound && (
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message="No site IO configuration exists for this site yet"
          description="Upload a JSON file or paste a configuration in the editor below, then click Save to create it."
        />
      )}

      {showEditor ? (
        <SiteIoConfigEditor
          initialData={liveConfiguration}
          onChange={setPendingData}
          site_id={site_id}
          undeclaredRotations={rotationDeclarations.undeclared}
        />
      ) : liveConfiguration ? (
        <SiteIoConfigCard
          configuration={liveConfiguration}
          undeclaredRotations={rotationDeclarations.undeclared}
        />
      ) : null}

      <SiteIoConfigDiffModal
        open={previewOpen}
        diff={diff}
        mode="preview"
        onCancel={() => setPreviewOpen(false)}
      />
      <SiteIoConfigDiffModal
        open={confirmOpen}
        diff={diff}
        mode="save"
        onCancel={() => setConfirmOpen(false)}
        onOk={handleSave}
        confirmLoading={isPending}
      />
    </div>
  );
};
